import { useState } from 'react';
import api from '../services/api';

/**
 * @module useUpdateQuestionnaire
 * @description Custom hook for updating questionnaires in the Mental Health Portal application.
 * Manages loading and error states, and provides a function for updating questionnaire data.
 *
 * @requires react
 * @requires ../services/api
 */

/**
 * Hook to update questionnaire data
 * @function useUpdateQuestionnaire
 * @returns {Object} Object containing updateQuestionnaire function, loading state, and error state
 */
export const useUpdateQuestionnaire = () => {
  /**
   * @state {boolean} loading - Indicates if the update operation is in progress
   */
  const [loading, setLoading] = useState(false);

  /**
   * @state {string|null} error - Error message if the update operation fails, null otherwise
   */
  const [error, setError] = useState(null);

  /**
   * Updates a questionnaire with new data
   * @async
   * @function updateQuestionnaire
   * @param {string|number} questionnaireId - ID of the questionnaire to update
   * @param {Object} updatedData - New data for the questionnaire
   * @returns {Promise<Object|null>} The updated questionnaire data if successful, null otherwise
   */
  const updateQuestionnaire = async (questionnaireId, updatedData) => {
    setLoading(true);
    setError(null);

    console.log('questionnaireId', questionnaireId);

    try {
      const { data } = await api.put(
        `questionnaire/${questionnaireId}`,
        updatedData
      );
      console.log('updatedData', updatedData);

      return data;
    } catch (err) {
      setError('Erro ao atualizar o questionário');
      return null;
    } finally {
      setLoading(false);
    }
  };

  /**
   * @typedef {Object} UseUpdateQuestionnaireReturn
   * @property {Function} updateQuestionnaire - Function to update a questionnaire
   * @property {boolean} loading - Indicates if the update operation is in progress
   * @property {string|null} error - Error message if the update operation fails, null otherwise
   */

  /**
   * @returns {UseUpdateQuestionnaireReturn} The hook's return values
   */
  return { updateQuestionnaire, loading, error };
};
